import { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { adminApi, Card, Badge } from './_shared';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminFeatureFlags() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const r = await adminApi.flags(); setFlags(r.flags || []); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const toggle = async (f) => {
    try { await adminApi.toggleFlag(f.key, !f.enabled); toast.success(`${f.label} ${!f.enabled ? 'enabled' : 'disabled'}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const grouped = flags.reduce((acc, f) => { (acc[f.category] = acc[f.category] || []).push(f); return acc; }, {});

  return (
    <AdminLayout title="Feature Flags" subtitle="Admin · Kill Switches & Rollouts">
      <p className="text-sm text-slate-400 mb-6 max-w-3xl">
        Instant on/off switches for platform features. Disable LLM commentary if Cognara is rate-limited,
        toggle public signup off during a security incident, or roll out experimental features in stages.
      </p>
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-500"/></div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <h2 className="text-[10px] tracking-[0.3em] text-slate-500 uppercase mb-3">{cat}</h2>
              <div className="space-y-2">
                {items.map(f => (
                  <Card key={f.key} className="p-4 flex items-center justify-between gap-4" data-testid={`flag-${f.key}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-slate-100">{f.label}</div>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5">{f.key}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <Badge tone={f.enabled ? 'green' : 'slate'}>{f.enabled ? 'Enabled' : 'Disabled'}</Badge>
                      <button onClick={() => toggle(f)} role="switch" aria-checked={f.enabled} data-testid={`flag-toggle-${f.key}`}
                        className={`relative w-12 h-6 rounded-full transition-colors ${f.enabled ? 'bg-[#1E4E8C]' : 'bg-slate-700'}`}>
                        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${f.enabled ? 'translate-x-6' : 'translate-x-0.5'}`}/>
                      </button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
